import { c as _c } from "react/compiler-runtime";
export { useFundingHostedWidgetUrl } from './checkout/hosted/hosted-url-react.js';
export { useAccountAvailableMethods } from './customer/available-methods-react.js';
export { useFundingCardAddition, useFundingCardRemoval } from './customer/card-management-react.js';
export { DirectPurchaseActionBoundary } from './direct-purchase/action-boundary-react.js';
export { useConnectedExchanges, useExchangeCheckout } from './exchange/exchange-react.js';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { throwIfAborted } from './abort.js';
export { useConnectedWallet, useConnectedWalletCapability } from './connected-wallet/connected-wallet-react.js';
export { useCryptoDeposit, useCryptoDepositCapability, useCryptoDepositSourcePrefetch } from './crypto-deposit/crypto-deposit-react.js';
import { fundingSessionQuery } from './customer/session-query.js';
import { contextForSession } from './discovery/context-query.js';
import { bindOnrampCache } from './operations/cache-lifecycle.js';
import { useFundingFunnel } from './operations/funnel-react.js';
import { hideFundingHistoryOperation } from './operations/hide-history.js';
import { fetchHistoryWithCooldown, historyRefetchInterval } from './operations/history-query.js';
import { useFundingNavigation } from './operations/navigation-react.js';
import { operationQuery, refreshOperationQuery } from './operations/operation-query.js';
import { fetchSavedFundingMethods } from './operations/saved-funding-methods.js';
import { watchOperation } from './operations/watch-operation.js';
/** Saved methods are payer-scoped, derived from successful Morfi funding history. */
export function useSavedFundingMethods(client) {
  const $ = _c(8);
  useDisposeCache(client);
  let t0;
  if ($[0] !== client.cacheKey) {
    t0 = ["onramp", client.cacheKey, "history", "saved-methods"];
    $[0] = client.cacheKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== client) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return fetchSavedFundingMethods(client, signal);
    };
    $[2] = client;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      persist: false
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 60000,
      retry: false,
      meta: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
/** Client instances isolate identity/environment caches; dispose on session changes. */
export function useOnrampHistory(client, status, realtime, category) {
  const $ = _c(26);
  useDisposeCache(client);
  const cache = useQueryClient();
  let t0;
  if ($[0] !== cache || $[1] !== client.cacheKey || $[2] !== realtime) {
    t0 = () => realtime?.subscribe(() => {
      cache.invalidateQueries({
        queryKey: ["onramp", client.cacheKey, "history"]
      });
    });
    $[0] = cache;
    $[1] = client.cacheKey;
    $[2] = realtime;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== cache || $[5] !== client || $[6] !== realtime) {
    t1 = [cache, client, realtime];
    $[4] = cache;
    $[5] = client;
    $[6] = realtime;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEffect(t0, t1);
  let t2;
  if ($[8] !== category || $[9] !== client.cacheKey || $[10] !== status) {
    t2 = ["onramp", client.cacheKey, "history", status, category];
    $[8] = category;
    $[9] = client.cacheKey;
    $[10] = status;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== category || $[13] !== client || $[14] !== status) {
    t3 = t4 => {
      const {
        pageParam,
        signal
      } = t4;
      return fetchHistoryWithCooldown(client, {
        limit: 50,
        ...(status ? {
          status
        } : {}),
        ...(category ? {
          category
        } : {}),
        ...(pageParam ? {
          cursor: pageParam
        } : {})
      }, signal);
    };
    $[12] = category;
    $[13] = client;
    $[14] = status;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      persist: false
    };
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== client) {
    t5 = () => historyRefetchInterval(client);
    $[17] = client;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t5) {
    t6 = {
      queryKey: t2,
      initialPageParam: undefined,
      queryFn: t3,
      getNextPageParam: _temp,
      retry: false,
      meta: t4,
      refetchInterval: t5
    };
    $[19] = t2;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const result = useInfiniteQuery(t6);
  const t7 = result.isPending ? "loading" : result.isError ? "error" : "ready";
  const t8 = result.isError ? "onramp_refresh_failed" : null;
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = {
      stage: "history",
      outcome: t7,
      errorCode: t8
    };
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  useFundingFunnel(client, t9);
  return result;
}
function _temp(lastPage) {
  return lastPage.hasMore ? lastPage.nextCursor : undefined;
}
export function useHideFundingHistoryOperation(client) {
  const $ = _c(6);
  useDisposeCache(client);
  const cache = useQueryClient();
  let t0;
  if ($[0] !== cache || $[1] !== client) {
    t0 = id => hideFundingHistoryOperation(client, cache, id);
    $[0] = cache;
    $[1] = client;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      persist: false
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0) {
    t2 = {
      mutationFn: t0,
      retry: false,
      meta: t1
    };
    $[4] = t0;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return useMutation(t2);
}
export function useOnrampOperation(client, operationId, options = {}) {
  useDisposeCache(client);
  const cache = useQueryClient();
  const {
    observeDelivery
  } = useFundingNavigation(client);
  const onError = useRef(options.onRefreshError);
  onError.current = options.onRefreshError;
  const result = useQuery({
    ...operationQuery(client, cache, operationId),
    enabled: options.active !== false
  });
  useEffect(() => {
    if (result.data?.status === 'delivered' && result.data.settlementEvidence === 'provider_reported') observeDelivery(operationId);
  }, [result.data, operationId, observeDelivery]);
  useFundingFunnel(client, {
    stage: 'receipt',
    operationId,
    outcome: result.isPending ? 'loading' : result.isError ? 'error' : 'ready',
    errorCode: result.isError ? 'onramp_refresh_failed' : null
  });
  useEffect(() => {
    if (options.active === false) return;
    const watcher = watchOperation({
      operationId,
      fetchOperation: async signal => refreshOperationQuery(client, cache, operationId, signal),
      subscribe: listener => options.realtime?.subscribe(listener) ?? (() => undefined),
      onUpdate: data => {
        client.observeFunnel?.({
          stage: 'receipt',
          outcome: 'ready',
          operationId
        });
        cache.setQueryData(['onramp', client.cacheKey, 'operation', operationId], previous => previous && previous.version >= data.version ? previous : data);
      },
      onError: code => {
        client.observeFunnel?.({
          stage: 'receipt',
          outcome: 'error',
          operationId,
          errorCode: code
        });
        onError.current?.(code);
      }
    });
    const unregister = client.onDispose(() => watcher.dispose());
    return () => {
      unregister();
      watcher.dispose();
    };
  }, [cache, client, operationId, options.active, options.realtime]);
  return result;
}
function useDisposeCache(client) {
  const $ = _c(4);
  const cache = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== cache || $[1] !== client) {
    t0 = () => {
      bindOnrampCache(client, cache);
    };
    t1 = [cache, client];
    $[0] = cache;
    $[1] = client;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
/** Resuming an owned operation never depends on MoonPay session availability. */
export function usePendingFundingOperation(client, t0, t1) {
  const $ = _c(12);
  const enabled = t0 === undefined ? true : t0;
  const navigationKey = t1 === undefined ? 0 : t1;
  useDisposeCache(client);
  const cache = useQueryClient();
  let t2;
  if ($[0] !== client.cacheKey || $[1] !== navigationKey) {
    t2 = ["onramp", client.cacheKey, "pending", navigationKey];
    $[0] = client.cacheKey;
    $[1] = navigationKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== cache || $[4] !== client || $[5] !== navigationKey) {
    t3 = async t4 => {
      const {
        signal
      } = t4;
      const context = await contextForSession(cache, client, signal, navigationKey);
      throwIfAborted(signal);
      if (!context.pendingId) {
        return null;
      }
      const operation = (await client.getOperation(context.pendingId, signal)).data;
      throwIfAborted(signal);
      if (operation.id !== context.pendingId) {
        throw new Error("onramp_operation_mismatch");
      }
      return ["created", "action_required", "awaiting_payment", "submission_unknown", "processing"].includes(operation.status) ? operation : null;
    };
    $[3] = cache;
    $[4] = client;
    $[5] = navigationKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      persist: false
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== enabled || $[9] !== t2 || $[10] !== t3) {
    t5 = {
      queryKey: t2,
      enabled,
      queryFn: t3,
      retry: false,
      staleTime: Infinity,
      gcTime: 0,
      refetchOnWindowFocus: false,
      meta: t4
    };
    $[8] = enabled;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return useQuery(t5);
}
/** Session tokens are visit-scoped, memory-only, never reusable after client disposal. */
export function useOnrampSession(client, t0) {
  const $ = _c(12);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  useDisposeCache(client);
  const cache = useQueryClient();
  let t2;
  if ($[2] !== cache || $[3] !== client || $[4] !== options.navigationKey) {
    t2 = fundingSessionQuery(client, cache, options.navigationKey);
    $[2] = cache;
    $[3] = client;
    $[4] = options.navigationKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = options.enabled !== false;
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const result = useQuery(t4);
  const t5 = result.isFetching ? "loading" : result.isError ? "error" : "ready";
  const t6 = result.isError ? "provider_unavailable" : null;
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      stage: "session",
      outcome: t5,
      errorCode: t6
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useFundingFunnel(client, t7, options.enabled !== false);
  return result;
}
export { useFundingAmountDraft } from './amount/amount-draft-react.js';
export { useFundingCurrency } from './amount/currency-react.js';
export { useBankTransferInstructions } from './bank-transfers/react.js';
export { useFundingCheckout } from './checkout/checkout-react.js';
export { useFundingInlineCheckout } from './checkout/inline-checkout-react.js';
export { useFundingQuotePreview } from './checkout/quote-preview-react.js';
export { useFundingAuthentication } from './customer/authentication-react.js';
export { useFundingAuthenticationTerms } from './customer/authentication-terms-react.js';
export { useContactVerification, useFundingProfileContact } from './customer/contact-react.js';
export { useCustomerKyc } from './customer/customer-kyc-react.js';
export { useFundingEntryWarmup } from './customer/entry-warmup-react.js';
export { useFundingProfile } from './customer/funding-profile-react.js';
export { useAccountMethodLimits } from './customer/method-limits-react.js';
export { useAccountPaymentMethods } from './customer/payment-methods-react.js';
export { useFundingResource } from './customer/resource-react.js';
export { useVerifiedPhoneKyc } from './customer/verified-phone-react.js';
export { useDirectPurchaseContext } from './direct-purchase/context-react.js';
export { useWithdrawalAmount } from './offramp/amount-react.js';
export { useWithdrawalCheckout } from './offramp/checkout-react.js';
export { useFundingActionRecovery } from './operations/action-recovery-react.js';
export { clearFundingProgress, useFundingHistoryNavigation, useFundingNavigation } from './operations/navigation-react.js';
export { useOnrampRealtime } from './operations/realtime-react.js';
export { useFundingWalletRefresh } from './operations/wallet-refresh-react.js';